'use client'

import React from 'react'
import Link from 'next/link'
import { useSession } from 'next-auth/react'
import { usePathname, useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar } from '@/components/ui/avatar'
import { Icons } from '@/components/ui/icons'
import { NotificationCenter } from '@/components/NotificationCenter'
import { openCommandPalette } from '@/components/CommandPalette'
import { useAI } from '@/context/AIContext'
import { useAssistantPersona } from '@/context/AssistantPersonaContext'
import { useBriefing } from '@/context/BriefingContext'
import { CoworkerAvatar } from '@/components/shell/CoworkerAvatar'
import { briefingNotifications } from '@/lib/briefing'
import { useRecordCrumb } from '@/hooks/useRecordCrumb'
import { breadcrumbsFor } from '@/lib/navigation'
import { sessionFirstName } from '@/lib/session-name'
import { useSidebar } from './Sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// Search: opens the command palette (Cmd K), as in the PA header.
function SearchInput() {
  return (
    <button
      type='button'
      onClick={openCommandPalette}
      aria-label='Search (Command K)'
      className={cn(
        'group flex h-9 w-64 items-center gap-2 px-3',
        'rounded-full border border-border/50 bg-white/50',
        'text-sm text-muted-foreground',
        'transition-all duration-300 ease-out',
        'hover:border-brand-cornflower/40 hover:bg-white/90 hover:shadow-sm',
        'hover:w-72',
        'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50'
      )}
    >
      <Icons.search
        aria-hidden='true'
        className='h-4 w-4'
        strokeWidth={1.5}
      />
      <span className='flex-1 text-left'>Search...</span>
      <kbd
        aria-hidden='true'
        className={cn(
          'hidden h-5 items-center gap-1 rounded px-1.5 sm:inline-flex',
          'text-xs font-medium',
          'border border-border/50 bg-muted/50 text-muted-foreground',
          'transition-all duration-200',
          'group-hover:border-brand-cornflower/30 group-hover:bg-brand-cornflower/10 group-hover:text-brand-navy'
        )}
      >
        <Icons.command className='h-3 w-3' />K
      </kbd>
    </button>
  )
}

/**
 * "Ask Ava" sits where the PA header has "AI Manager", same navy pill, and opens
 * the chat panel (Cmd J, handled in AIContext). The name comes from the
 * assistant_persona_name Setting (plan 03 CHG-05, CHG-18).
 */
function AskPersonaTrigger() {
  const { openManager, hasPendingSuggestions, isManagerOpen } = useAI()
  const persona = useAssistantPersona()
  const label = `Ask ${persona.name}`

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <button
            type='button'
            onClick={openManager}
            className={cn(
              'relative flex items-center gap-2 py-1.5 pl-1.5 pr-3',
              'rounded-full',
              'text-sm font-medium',
              'transition-all duration-200',
              'bg-gradient-to-r from-brand-navy to-brand-purple',
              'text-white',
              'shadow-md shadow-brand-navy/20',
              'hover:shadow-lg hover:shadow-brand-purple/30',
              isManagerOpen && 'ring-2 ring-brand-cornflower ring-offset-2',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower focus:ring-offset-2'
            )}
            aria-label={`${label} (Command J)`}
            aria-expanded={isManagerOpen}
          >
            {/* Ava's face at 20px (plan 03 CHG-14, D7); the pill's label names her. */}
            <CoworkerAvatar name={persona.name} photo={persona.photo} size={20} alt='' className='ring-1 ring-white/40 rounded-full' />
            <span className='hidden sm:inline'>{label}</span>
            <kbd
              aria-hidden='true'
              className={cn(
                'hidden items-center gap-0.5 sm:inline-flex',
                'rounded px-1.5 py-0.5',
                'bg-white/20 text-white/90',
                'text-xs font-medium'
              )}
            >
              <Icons.command className='h-2.5 w-2.5' />J
            </kbd>
            {hasPendingSuggestions && (
              <span
                aria-hidden='true'
                className={cn(
                  'absolute -right-1 -top-1',
                  'h-3 w-3 rounded-full',
                  'border-2 border-white bg-brand-cornflower',
                  'animate-pulse'
                )}
              />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side='bottom' className='sm:hidden'>
          <span>{label}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// User menu: initials and first name (plan 03 CHG-18, D8).
function UserMenu() {
  const { data: session } = useSession()
  const router = useRouter()
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''

  if (!session?.user) return null

  const firstName = sessionFirstName(session)
  const handleLogout = () => {
    window.location.href = `${basePath}/api/auth/logout`
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          aria-label={`Account menu for ${session.user.name || session.user.email || 'you'}`}
          className={cn(
            'group flex items-center gap-1 rounded-full',
            'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50 focus:ring-offset-2',
            'transition-transform duration-200'
          )}
        >
          <div className='flex items-center gap-3'>
            <Avatar
              src={session.user.image}
              fallback={session.user.name || session.user.email || '?'}
              size='md'
              showRing
            />
            {firstName && (
              <span className='hidden text-sm font-medium text-foreground lg:inline'>{firstName}</span>
            )}
            <Icons.chevronDown
              aria-hidden='true'
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform duration-200',
                'group-hover:translate-y-0.5 group-data-[state=open]:rotate-180'
              )}
            />
          </div>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align='end' className='w-64'>
        {/* User info header */}
        <div className='border-b border-border/50 px-3 py-3'>
          <div className='flex items-center gap-3'>
            <Avatar
              src={session.user.image}
              fallback={session.user.name || session.user.email || '?'}
              size='md'
            />
            <div className='min-w-0 flex-1'>
              <p className='truncate text-sm font-medium text-foreground'>{session.user.name}</p>
              <p className='truncate text-xs text-muted-foreground'>{session.user.email}</p>
            </div>
          </div>
        </div>

        <div className='py-1'>
          <DropdownMenuItem className='gap-3 rounded-lg px-3 py-2.5' onClick={() => router.push('/settings')}>
            <Icons.settings className='h-4 w-4 text-muted-foreground' strokeWidth={1.5} />
            <span>Settings</span>
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator />

        <div className='py-1'>
          <DropdownMenuItem onClick={handleLogout} destructive className='gap-3 rounded-lg px-3 py-2.5'>
            <Icons.logout className='h-4 w-4' strokeWidth={1.5} />
            <span>Sign out</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface HeaderProps {
  onOpenMobileMenu?: () => void
}

/** The bell lists what the Command Briefing says needs you (plan 03 CHG-13). */
function BriefingBell() {
  const { briefing } = useBriefing()
  const notifications = React.useMemo(() => briefingNotifications(briefing, new Date()), [briefing])
  return <NotificationCenter notifications={notifications} />
}

export function Header({ onOpenMobileMenu }: HeaderProps) {
  const pathname = usePathname()
  const recordLabel = useRecordCrumb(pathname)
  const breadcrumbs = breadcrumbsFor(pathname, recordLabel)
  const { isCollapsed } = useSidebar()

  return (
    <header
      role='banner'
      className={cn(
        // Floating pill positioning (PA header bar)
        'fixed right-4 top-4 z-sticky',
        // The left edge follows the sidebar: 17rem expanded, 5rem collapsed
        // (plan 03 CHG-18). It used to stay at 17rem and leave a 12rem gap.
        'left-4',
        isCollapsed ? 'md:left-[calc(4rem+1rem)]' : 'md:left-[calc(16rem+1rem)]',
        'transition-[left] duration-300 ease-out',
        // White raised bar over the page wash
        'rounded-2xl bg-white/80 backdrop-blur-xl',
        'border border-white/60 ring-1 ring-black/[0.03]',
        'shadow-float',
        // Layout
        'flex items-center justify-between',
        'h-14 px-4 lg:px-6'
      )}
    >
      {/* Left: Mobile menu + Breadcrumb */}
      <div className='flex min-w-0 items-center gap-2'>
        <Button
          variant='ghost'
          size='icon-sm'
          onClick={onOpenMobileMenu}
          className='-ml-1 text-muted-foreground hover:text-foreground md:hidden'
          aria-label='Open navigation menu'
        >
          <Icons.menu className='h-5 w-5' strokeWidth={1.5} />
        </Button>

        <Icons.home aria-hidden='true' className='hidden h-4 w-4 text-muted-foreground md:block' strokeWidth={1.5} />
        <nav aria-label='Breadcrumb' className='hidden min-w-0 sm:block'>
          <ol className='flex items-center gap-1 text-sm'>
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1
              return (
                <li key={crumb.href} className='flex min-w-0 items-center gap-1'>
                  {index > 0 && (
                    <Icons.chevronRight className='h-4 w-4 shrink-0 text-muted-foreground/60' aria-hidden='true' />
                  )}
                  {isLast ? (
                    <span className='truncate font-medium text-foreground' aria-current='page'>
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      href={crumb.href}
                      className='truncate rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
                    >
                      {crumb.label}
                    </Link>
                  )}
                </li>
              )
            })}
          </ol>
        </nav>
        {/* Mobile: Just show current page */}
        <span className='truncate text-sm font-medium text-foreground sm:hidden'>
          {breadcrumbs[breadcrumbs.length - 1].label}
        </span>
      </div>

      {/* Right: Actions */}
      <div className='flex items-center gap-1 sm:gap-2'>
        <div className='hidden lg:block'>
          <SearchInput />
        </div>

        <Button
          variant='ghost'
          size='icon-sm'
          onClick={openCommandPalette}
          className='text-muted-foreground hover:text-foreground lg:hidden'
          aria-label='Search'
        >
          <Icons.search className='h-5 w-5' strokeWidth={1.5} />
        </Button>

        <AskPersonaTrigger />

        <BriefingBell />

        <div aria-hidden='true' className='mx-1 hidden h-6 w-px bg-border/60 lg:block' />

        <UserMenu />
      </div>
    </header>
  )
}
